import { Fragment, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";

import { Account, isEmployeeRole } from "../../../api/accounts";
import { AreaOfFraud } from "../../../api/cases";
import { Employee } from "../../../api/creditAccounts";
import { RelatedAs } from "../../../api/stakeholder_relationships";
import AccountsSection, { INLINE_ROW_LIMIT } from "./party-involved/AccountsSection";
import AccountPartiesSection from "./party-involved/AccountPartiesSection";
import EmployeeSection from "./party-involved/EmployeeSection";
import { isAccountRowValid } from "./party-involved/AccountRow";
import { AddAccountPartyRow, isAccountPartyValid } from "./party-involved/AccountPartyRow";
import { SectionIssue } from "./party-involved/derive";
import StepNavigation from "./StepNavigation";
import { isEmployeeRowValid, isEmployeesSectionValid } from "../utils/creditValidation";

export interface AccountPartyState {
  accounts: Account[];
  primaryParties: AddAccountPartyRow[];
}

export interface AccountPartyInvolvedStepProps {
  areaOfFraud: AreaOfFraud;
  accountParty: AccountPartyState;
  updateAccountParty: (patch: Partial<AccountPartyState>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const deriveAccountEmployees = (
  parties: AddAccountPartyRow[],
  areaOfFraud: AreaOfFraud,
): Employee[] =>
  parties
    .filter((p) =>
      (p.relationshipGroups || []).some(
        (r) => r.relatedAs && isEmployeeRole(r.relatedAs, areaOfFraud),
      ),
    )
    .map((p) => {
      const employeeRelations = (p.relationshipGroups || []).filter(
        (r) => r.relatedAs && isEmployeeRole(r.relatedAs, areaOfFraud),
      );
      return {
        key: p.stakeholder.stakeholder_id,
        types: employeeRelations.map((r) => r.relatedAs as RelatedAs),
        name: p.stakeholder.name,
        employeeId: p.employee_details?.employee_id,
      };
    });

const hasAccountInput = (account: Account): boolean =>
  Boolean(
    account.ext_account_number.trim() ||
      account.account_name.trim() ||
      account.account_type ||
      account.account_category,
  );

const AccountPartyInvolvedStep: React.FC<AccountPartyInvolvedStepProps> = ({
  areaOfFraud,
  accountParty,
  updateAccountParty,
  onNext,
  onPrevious,
}) => {
  const { accounts, primaryParties } = accountParty;

  const employees = useMemo(
    () => deriveAccountEmployees(primaryParties, areaOfFraud),
    [primaryParties, areaOfFraud],
  );

  const accountsWithInput = accounts.filter(hasAccountInput);
  const invalidAccounts = accountsWithInput.filter((r) => !isAccountRowValid(r, accounts));
  const accountsValid = invalidAccounts.length === 0;
  const partiesValid = primaryParties.every((p) => isAccountPartyValid(p, primaryParties));
  const employeesValid = isEmployeesSectionValid(employees);
  const stepValid = accountsValid && partiesValid && employeesValid;

  const sectionIssues: SectionIssue[] = useMemo(
    () => [
      {
        key: "accounts",
        label: "Accounts",
        flagged: !accountsValid,
        incompleteRows: invalidAccounts.length,
        hiddenInvalid: accounts.filter(
          (r, i) => i >= INLINE_ROW_LIMIT && hasAccountInput(r) && !isAccountRowValid(r, accounts),
        ).length,
      },
      {
        key: "primaryParties",
        label: "Primary Parties",
        flagged: !partiesValid,
        incompleteRows: primaryParties.filter(
          (p) => !isAccountPartyValid(p, primaryParties),
        ).length,
        hiddenInvalid: primaryParties.filter(
          (p, i) => i >= INLINE_ROW_LIMIT && !isAccountPartyValid(p, primaryParties),
        ).length,
      },
      {
        key: "employees",
        label: "Employee",
        flagged: !employeesValid,
        incompleteRows: employees.filter((e) => !isEmployeeRowValid(e)).length,
        hiddenInvalid: employees.filter(
          (e, i) => i >= INLINE_ROW_LIMIT && !isEmployeeRowValid(e),
        ).length,
      },
    ],
    [accounts, accountsValid, invalidAccounts.length, primaryParties, partiesValid, employees, employeesValid],
  );

  const flaggedSections = sectionIssues.filter((s) => s.flagged);

  const [showErrors, setShowErrors] = useState(false);
  const [focus, setFocus] = useState<{ key: string; seq: number }>({
    key: "",
    seq: 0,
  });

  const focusSection = (key: string) => {
    setFocus((prev) => ({ key, seq: prev.seq + 1 }));
  };

  const handleNext = () => {
    if (!stepValid) {
      setShowErrors(true);
      if (flaggedSections.length > 0) focusSection(flaggedSections[0].key);
      return;
    }
    setShowErrors(false);
    if (accountsWithInput.length !== accounts.length) {
      updateAccountParty({ accounts: accountsWithInput });
    }
    onNext();
  };

  const handleEmployeeIdChange = (key: string, employeeId: string) => {
    updateAccountParty({
      primaryParties: primaryParties.map((p) =>
        p.stakeholder.stakeholder_id === key
          ? {
              ...p,
              employee_details: { ...p.employee_details, employee_id: employeeId },
            }
          : p,
      ),
    });
  };

  const handleAccountsChange = (next: Account[]) => {
    const accountNameToAccountNumbers = new Map<string, string[]>();
    next.forEach((acc) => {
      const name = acc.account_name.trim();
      if (!name || !acc.ext_account_number.trim()) return;
      if (!accountNameToAccountNumbers.has(name)) {
        accountNameToAccountNumbers.set(name, []);
      }
      accountNameToAccountNumbers.get(name)!.push(acc.account_number);
    });


    const validParties = primaryParties.filter((party) => {
      const isPrimaryAccountHolder = (party.relationshipGroups || []).some(
        (r) => r.relatedAs === RelatedAs.PRIMARY_ACCOUNT_HOLDER,
      );
      if (
        isPrimaryAccountHolder &&
        (accountNameToAccountNumbers.get(party.stakeholder.name.trim()) || []).length === 0
      ) {
        return false;
      }
      return true;
    });

    const activeAccountNumbers = new Set(next.map((n) => n.account_number));

    const updatedParties: AddAccountPartyRow[] = validParties.map((party) => {
      const updatedRelationships = party.relationshipGroups.map(
        (rel) => (rel.relatedAs === RelatedAs.PRIMARY_ACCOUNT_HOLDER ?
          {
            ...rel,
            accountNumbers: accountNameToAccountNumbers.get(party.stakeholder.name.trim()) || [],
          } : {
            ...rel,
            accountNumbers: rel.accountNumbers.filter((num) => activeAccountNumbers.has(num)),
          }
        )
      )
      return {
        ...party,
        relationshipGroups: updatedRelationships
      };
    });

    updateAccountParty({
      accounts: next,
      primaryParties: updatedParties,
    });
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <AccountsSection
          accounts={accounts}
          onChange={handleAccountsChange}
          showErrors={showErrors}
          issue={sectionIssues[0]}
          focus={focus}
        />

        <AccountPartiesSection
          parties={primaryParties}
          onPartiesChange={(next) => updateAccountParty({ primaryParties: next })}
          accounts={accounts}
          area_of_fraud={areaOfFraud}
          showErrors={showErrors}
          issue={sectionIssues[1]}
          focus={focus}
        />

        <EmployeeSection
          employees={employees}
          onEmployeeIdChange={handleEmployeeIdChange}
          showErrors={showErrors}
          issue={sectionIssues[2]}
          focus={focus}
        />
      </div>

      {showErrors && flaggedSections.length > 0 && (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          <AlertCircle width={16} height={16} className="mt-0.5 shrink-0" />
          <span>
            Complete the required fields in{" "}
            {flaggedSections.map((s, i) => (
              <Fragment key={s.key}>
                <button
                  type="button"
                  className="font-medium underline underline-offset-2 hover:text-red-800"
                  onClick={() => focusSection(s.key)}
                >
                  {s.label}
                  {s.incompleteRows > 0 ? ` (${s.incompleteRows})` : ""}
                </button>
                {i < flaggedSections.length - 1 ? ", " : ""}
              </Fragment>
            ))}
            .
          </span>
        </div>
      )}

      <StepNavigation onNext={handleNext} onPrevious={onPrevious} />
    </>
  );
};

export default AccountPartyInvolvedStep;
